import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Mail, MessageCircle, Landmark, CheckCircle2, XCircle, Send, Plug, Cake } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusPill = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`} data-testid={`status-${label}`}>
    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
    {ok ? "Configurado" : "Não configurado"}
  </span>
);

const TPL_STATUS = {
  APPROVED: ["Aprovado", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  PENDING: ["Em análise", "bg-amber-50 text-amber-700 border-amber-200"],
  IN_APPEAL: ["Em recurso", "bg-amber-50 text-amber-700 border-amber-200"],
  REJECTED: ["Rejeitado", "bg-red-50 text-red-700 border-red-200"],
  PAUSED: ["Pausado", "bg-slate-100 text-slate-600 border-slate-200"],
  DISABLED: ["Desativado", "bg-slate-100 text-slate-600 border-slate-200"],
};

const TemplateMeta = ({ info }) => {
  if (!info) return null;
  const [rot, cls] = info.existe ? (TPL_STATUS[info.status] || [info.status, "bg-slate-100 text-slate-600 border-slate-200"]) : ["Não cadastrado na Meta", "bg-slate-100 text-slate-500 border-slate-200"];
  return (
    <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2 leading-relaxed space-y-1" data-testid={`tpl-meta-${info.tipo}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border font-medium ${cls}`} data-testid={`tpl-meta-status-${info.tipo}`}>{rot}</span>
        {info.categoria && <span className="text-slate-400">{info.categoria} · pt_BR</span>}
      </div>
      {info.motivo && info.motivo !== "NONE" && <div className="text-red-600">Motivo: {info.motivo}</div>}
      <div><span className="font-medium">Texto:</span> <i>{info.texto}</i></div>
    </div>
  );
};

export default function Integracoes() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [tel, setTel] = useState("");
  const [testando, setTestando] = useState(null);
  const [inicioMes, setInicioMes] = useState("");
  const [diasFech, setDiasFech] = useState(5);
  const [pixChave, setPixChave] = useState("");
  const [tplRes, setTplRes] = useState("");
  const [tplEq, setTplEq] = useState("");
  const [tplReaj, setTplReaj] = useState("");
  const [enviandoAniv, setEnviandoAniv] = useState(false);
  const [resultAniv, setResultAniv] = useState(null);
  const [tplStatus, setTplStatus] = useState(null);
  const [cadastrandoTpl, setCadastrandoTpl] = useState(false);
  const [telTpl, setTelTpl] = useState("");
  const [testandoTpl, setTestandoTpl] = useState(null);
  const [resultTpl, setResultTpl] = useState(null);

  const testarTemplate = async (tipo) => {
    if (!telTpl.trim()) return toast.error("Informe um telefone com DDD.");
    setTestandoTpl(tipo); setResultTpl(null);
    try {
      const { data } = await axios.post(`${API}/aniversarios/templates/testar`, { telefone: telTpl.trim(), tipo });
      setResultTpl(data);
      if (data.ok) toast.success("Mensagem de teste enviada.");
      else if (data.agendado) { toast.success("Teste agendado: será enviado quando a Meta aprovar."); loadTplStatus(); }
      else toast.error(data.erro || "Falha no envio.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao enviar teste."); } finally { setTestandoTpl(null); }
  };

  const loadTplStatus = async () => {
    try { const { data } = await axios.get(`${API}/aniversarios/templates`); setTplStatus(data); } catch (e) { /* silencioso */ }
  };
  const cadastrarTemplates = async () => {
    setCadastrandoTpl(true);
    try {
      const { data } = await axios.post(`${API}/aniversarios/templates/cadastrar`);
      setTplStatus(data);
      const falhas = (data.resultados || []).filter((r) => !r.ok);
      if (falhas.length) toast.error(falhas.map((f) => `${f.nome}: ${f.detalhe}`).join(" · "));
      else toast.success("Templates enviados para aprovação na Meta.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao cadastrar na Meta."); } finally { setCadastrandoTpl(false); }
  };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/integracoes/config`);
      setCfg(data);
      setInicioMes(data.inicioFaturamento || "");
      setDiasFech(data.fechamentoDiasAntes ?? 5);
      setPixChave(data.pixChave || "");
      setTplRes(data.templateAniversarioResidente || "");
      setTplEq(data.templateAniversarioEquipe || "");
      setTplReaj(data.templateReajuste || "");
    } catch (e) { toast.error("Falha ao carregar as integrações."); }
  };
  useEffect(() => { load(); loadTplStatus(); }, []);

  const salvarInicio = async () => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(inicioMes)) return toast.error("Selecione um mês válido.");
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/integracoes/config`, { inicioFaturamento: inicioMes });
      setCfg(data);
      setInicioMes(data.inicioFaturamento || "");
      toast.success(`Início do faturamento definido para ${inicioMes}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar.");
    } finally { setSaving(false); }
  };

  const salvarDias = async () => {
    const n = parseInt(diasFech, 10);
    if (Number.isNaN(n) || n < 0 || n > 27) return toast.error("Informe um número entre 0 e 27.");
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/integracoes/config`, { fechamentoDiasAntes: n });
      setCfg(data);
      setDiasFech(data.fechamentoDiasAntes ?? n);
      toast.success(`Fechamento definido para ${n} dia(s) antes do pagamento.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar.");
    } finally { setSaving(false); }
  };

  const salvarPix = async () => {
    const v = (pixChave || "").replace(/\s/g, "");
    if (v.length < 5) return toast.error("Informe uma chave Pix válida.");
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/integracoes/config`, { pixChave: v });
      setCfg(data);
      setPixChave(data.pixChave || v);
      toast.success("Chave Pix salva. Os PDFs de fatura passam a usar esta chave.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar.");
    } finally { setSaving(false); }
  };

  const toggle = async (campo, valor) => {
    setSaving(true);
    setCfg((c) => ({ ...c, [campo]: valor }));
    try {
      const { data } = await axios.put(`${API}/integracoes/config`, { [campo]: valor });
      setCfg(data);
      const nomes = { envioEmailAtivo: "Envio automático de e-mail", envioWhatsappAtivo: "Envio automático de WhatsApp", asaasAtivo: "Faturamento / boletos (Asaas)" };
      toast.success(`${nomes[campo] || "Configuração"} ${valor ? "ATIVADO" : "desativado"}.`);
    } catch (e) {
      toast.error("Não foi possível salvar.");
      load();
    } finally { setSaving(false); }
  };

  const salvarTemplates = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/integracoes/config`, { templateAniversarioResidente: tplRes, templateAniversarioEquipe: tplEq, templateReajuste: tplReaj });
      setCfg(data); toast.success("Templates de aniversário salvos."); loadTplStatus();
    } catch (e) { toast.error("Não foi possível salvar."); } finally { setSaving(false); }
  };
  const enviarAniversariosHoje = async () => {
    setEnviandoAniv(true);
    try {
      const { data } = await axios.post(`${API}/aniversarios/enviar-hoje`);
      setResultAniv(data);
      toast.success(`Parabéns enviados: ${data.enviados}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao enviar."); } finally { setEnviandoAniv(false); }
  };

  const setAviso = async (apiKey, cfgKey, v) => {
    setSaving(true);
    setCfg((c) => ({ ...c, avisos: { ...(c.avisos || {}), [cfgKey]: v } }));
    try {
      const { data } = await axios.put(`${API}/integracoes/config`, { [apiKey]: v });
      setCfg(data);
    } catch (e) {
      toast.error("Não foi possível salvar.");
      load();
    } finally { setSaving(false); }
  };


  const testarEmail = async () => {
    if (!email.trim()) return toast.error("Informe um e-mail.");
    setTestando("email");
    try {
      const { data } = await axios.post(`${API}/integracoes/testar-email`, { email: email.trim() });
      if (data.ok) toast.success(`E-mail de teste enviado para ${data.para}. Confira a caixa de entrada.`);
      else toast.error(`Falha: ${data.erro || data.status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao enviar."); }
    finally { setTestando(null); }
  };

  const testarWhatsapp = async () => {
    if (!tel.trim()) return toast.error("Informe um telefone com DDD.");
    setTestando("whatsapp");
    try {
      const { data } = await axios.post(`${API}/integracoes/testar-whatsapp`, { telefone: tel.trim() });
      if (data.ok) toast.success(`WhatsApp de teste enviado para ${data.para}. Confira o aparelho.`);
      else toast.error(`Falha (${data.status}): ${data.erro || ""}${data.dica ? " — " + data.dica : ""}`, { duration: 12000 });
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao enviar."); }
    finally { setTestando(null); }
  };

  if (!cfg) return <div className="p-8 text-slate-500" data-testid="integracoes-loading">Carregando...</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="integracoes-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center"><Plug className="w-6 h-6" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Integrações</h1>
          <p className="text-sm text-slate-500">Ative ou desative os envios automáticos e faça testes antes de ligar para todos os clientes.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card><CardContent className="pt-5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-700"><Mail className="w-4 h-4" /> E-mail (SMTP)</div>
          <StatusPill ok={cfg.emailConfigurado} label="email" />
        </CardContent></Card>
        <Card><CardContent className="pt-5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-700"><MessageCircle className="w-4 h-4" /> WhatsApp
            {cfg.whatsappConta?.numero && (
              <span className="text-xs text-slate-500 ml-1" data-testid="wa-numero-conectado">
                · Número conectado: <b>{cfg.whatsappConta.numero}</b> ({cfg.whatsappConta.nome}) · modo <b className={cfg.whatsappConta.modo === "LIVE" ? "text-emerald-600" : "text-amber-600"}>{cfg.whatsappConta.modo || "?"}</b>
              </span>
            )}
          </div>
          <StatusPill ok={cfg.whatsappConfigurado} label="whatsapp" />
        </CardContent></Card>
        <Card><CardContent className="pt-5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-700"><Landmark className="w-4 h-4" /> Asaas <span className="text-xs text-slate-400">({cfg.asaasAmbiente})</span></div>
          <StatusPill ok={cfg.asaasConfigurado} label="asaas" />
        </CardContent></Card>
      </div>

      <Tabs defaultValue="config">
        <TabsList data-testid="integracoes-tabs">
          <TabsTrigger value="config" data-testid="tab-config">Ativação</TabsTrigger>
          <TabsTrigger value="teste" data-testid="tab-teste">Teste de envio</TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="space-y-4 pt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Envios automáticos</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <div>
                  <div className="font-medium text-slate-800 flex items-center gap-2"><Mail className="w-4 h-4" /> E-mail automático</div>
                  <p className="text-sm text-slate-500">Envia faturas, boletos e o aviso de acesso ao portal por e-mail.</p>
                </div>
                <Switch checked={cfg.envioEmailAtivo} disabled={saving || !cfg.emailConfigurado} onCheckedChange={(v) => toggle("envioEmailAtivo", v)} data-testid="switch-email" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-slate-800 flex items-center gap-2"><MessageCircle className="w-4 h-4" /> WhatsApp automático</div>
                  <p className="text-sm text-slate-500">Envia faturas, boletos e avisos pelo WhatsApp aos responsáveis.</p>
                </div>
                <Switch checked={cfg.envioWhatsappAtivo} disabled={saving || !cfg.whatsappConfigurado} onCheckedChange={(v) => toggle("envioWhatsappAtivo", v)} data-testid="switch-whatsapp" />
              </div>
              <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                <div>
                  <div className="font-medium text-slate-800 flex items-center gap-2"><Landmark className="w-4 h-4" /> Faturamento / boletos (Asaas) <span className="text-xs text-slate-400">({cfg.asaasAmbiente})</span></div>
                  <p className="text-sm text-slate-500">Enquanto desativado, o sistema <b>não gera nem envia</b> nenhuma fatura ou boleto. Ative só depois de cadastrar produtos, valores de contrato e serviços extras.</p>
                </div>
                <Switch checked={cfg.asaasAtivo} disabled={saving || !cfg.asaasConfigurado} onCheckedChange={(v) => toggle("asaasAtivo", v)} data-testid="switch-asaas" />
              </div>
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                ⚠️ Mantenha o <b>Faturamento (Asaas)</b> e os envios desativados até cadastrar todos os dados reais. Com o Asaas desligado, nenhum boleto é criado. Use a aba "Teste de envio" para validar nos seus contatos.
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Avisos ao cliente</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-500">
                Escolha quais avisos são enviados aos responsáveis e por qual canal. Depende dos interruptores mestres de
                <b> E-mail</b> e <b>WhatsApp</b> acima estarem ligados.
              </p>
              {[
                { rot: "Boleto / Fatura (fatura criada + boleto)", wa: ["avisoFaturaWhatsapp", "faturaWhatsapp"], em: ["avisoFaturaEmail", "faturaEmail"] },
                { rot: "Lembrete antes do vencimento (2 dias antes: fatura com Pix e valor em aberto · no dia: boleto)", wa: ["avisoLembreteWhatsapp", "lembreteWhatsapp"], em: null },
                { rot: "Acesso ao Portal da Família", wa: null, em: ["avisoPortalEmail", "portalEmail"] },
                { rot: "Parabéns de aniversário (residentes, funcionários e profissionais)", wa: ["avisoAniversarioWhatsapp", "aniversarioWhatsapp"], em: null },
                { rot: "Aviso de reajuste da mensalidade (30 dias antes da renovação)", wa: ["avisoReajusteWhatsapp", "reajusteWhatsapp"], em: null },
              ].map((row) => (
                <div key={row.rot} className="flex items-center justify-between border-b border-slate-100 pb-3 last:border-0 last:pb-0" data-testid={`aviso-row-${row.rot}`}>
                  <div className="font-medium text-slate-700 text-sm">{row.rot}</div>
                  <div className="flex items-center gap-5">
                    <div className="flex items-center gap-2">
                      <MessageCircle className={`w-4 h-4 ${row.wa ? "text-emerald-600" : "text-slate-300"}`} />
                      {row.wa ? (
                        <Switch checked={!!cfg.avisos?.[row.wa[1]]} disabled={saving || !cfg.envioWhatsappAtivo}
                          onCheckedChange={(v) => setAviso(row.wa[0], row.wa[1], v)} data-testid={`switch-${row.wa[1]}`} />
                      ) : <span className="text-xs text-slate-300 w-9 text-center">—</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className={`w-4 h-4 ${row.em ? "text-blue-600" : "text-slate-300"}`} />
                      {row.em ? (
                        <Switch checked={!!cfg.avisos?.[row.em[1]]} disabled={saving || !cfg.envioEmailAtivo}
                          onCheckedChange={(v) => setAviso(row.em[0], row.em[1], v)} data-testid={`switch-${row.em[1]}`} />
                      ) : <span className="text-xs text-slate-300 w-9 text-center">—</span>}
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-6 text-xs text-slate-400 pt-1">
                <span className="flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</span>
                <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> E-mail</span>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="aniversario-card">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Cake className="w-4 h-4 text-pink-600" /> Parabéns de aniversário e aviso de reajuste (WhatsApp)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-500">
                Todos os dias às <b>09:00</b> o sistema envia feliz aniversário: ao <b>responsável</b> do residente e ao próprio <b>funcionário/profissional</b>
                (é preciso ter WhatsApp cadastrado). A Meta exige <b>templates aprovados</b>: use o botão abaixo para cadastrá-los direto na conta Meta (idioma pt_BR) ou crie-os no WhatsApp Manager com os nomes indicados.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Template — residente (enviado ao responsável)</Label>
                  <Input value={tplRes} onChange={(e) => setTplRes(e.target.value)} data-testid="tpl-aniversario-residente" />
                  <TemplateMeta info={tplStatus?.itens?.find((i) => i.tipo === "residente")} />
                </div>
                <div className="space-y-1.5">
                  <Label>Template — funcionário / profissional</Label>
                  <Input value={tplEq} onChange={(e) => setTplEq(e.target.value)} data-testid="tpl-aniversario-equipe" />
                  <TemplateMeta info={tplStatus?.itens?.find((i) => i.tipo === "equipe")} />
                </div>
              </div>
              {tplStatus?.erro && <p className="text-xs text-red-600" data-testid="tpl-meta-erro">Não foi possível consultar a Meta: {tplStatus.erro}</p>}
              <div className="space-y-1.5" data-testid="reajuste-template">
                <Label>Template — aviso de reajuste (enviado ao responsável 30 dias antes da renovação, às 09:10)</Label>
                <Input value={tplReaj} onChange={(e) => setTplReaj(e.target.value)} data-testid="tpl-reajuste" />
                <TemplateMeta info={tplStatus?.itens?.find((i) => i.tipo === "reajuste")} />
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Button variant="outline" disabled={saving} onClick={salvarTemplates} data-testid="salvar-tpl-aniversario">Salvar templates</Button>
                <Button variant="outline" className="gap-2 text-pink-700 border-pink-200 hover:bg-pink-50" disabled={cadastrandoTpl || !tplStatus || !!tplStatus.erro || (tplStatus.itens || []).every((i) => i.existe && i.status !== "REJECTED")} onClick={cadastrarTemplates} data-testid="cadastrar-tpl-meta">
                  <Plug className="w-4 h-4" /> {cadastrandoTpl ? "Cadastrando..." : "Cadastrar templates na Meta"}
                </Button>
                <Button variant="ghost" size="sm" onClick={loadTplStatus} data-testid="atualizar-tpl-meta">Atualizar status</Button>
                <Button className="gap-2 bg-pink-600 hover:bg-pink-700" disabled={enviandoAniv || !cfg.envioWhatsappAtivo || !cfg.avisos?.aniversarioWhatsapp} onClick={enviarAniversariosHoje} data-testid="enviar-aniversarios-hoje">
                  <Send className="w-4 h-4" /> {enviandoAniv ? "Enviando..." : "Enviar parabéns de hoje agora"}
                </Button>
                {(!cfg.envioWhatsappAtivo || !cfg.avisos?.aniversarioWhatsapp) && <span className="text-xs text-amber-600">Ligue o WhatsApp e o aviso de aniversário acima.</span>}
              </div>
              <div className="flex flex-wrap items-end gap-2 pt-3 border-t border-slate-100" data-testid="tpl-teste-box">
                <div className="space-y-1.5 flex-1 min-w-[180px]">
                  <Label>Enviar mensagem de teste (com dados fictícios) para</Label>
                  <Input placeholder="11999998888" value={telTpl} onChange={(e) => setTelTpl(e.target.value)} data-testid="tpl-teste-telefone" />
                </div>
                {[["residente", "Aniversário residente"], ["equipe", "Aniversário equipe"], ["reajuste", "Aviso de reajuste"]].map(([tipo, rot]) => {
                  const it = tplStatus?.itens?.find((i) => i.tipo === tipo);
                  const pend = ["PENDING", "IN_APPEAL"].includes(it?.status);
                  return (
                    <Button key={tipo} variant="outline" size="sm" className="gap-1.5" title={pend ? "Em análise na Meta: o teste será agendado e enviado após a aprovação" : ""} disabled={!!testandoTpl || !cfg.whatsappConfigurado || !(it?.status === "APPROVED" || pend)} onClick={() => testarTemplate(tipo)} data-testid={`tpl-teste-${tipo}`}>
                      <Send className="w-3.5 h-3.5" /> {testandoTpl === tipo ? "Enviando..." : rot}{pend ? " (agendar)" : ""}
                    </Button>
                  );
                })}
              </div>
              {(tplStatus?.testesPendentes || []).length > 0 && (
                <div className="text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 space-y-0.5" data-testid="tpl-teste-pendentes">
                  {tplStatus.testesPendentes.map((p, i) => (
                    <div key={i}>Teste agendado: <b>{p.template}</b> para {p.telefone} — será enviado automaticamente assim que a Meta aprovar (verificação a cada 10 min).</div>
                  ))}
                </div>
              )}
              {(tplStatus?.testesResultados || []).length > 0 && (
                <div className="text-xs rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="tpl-teste-historico">
                  {tplStatus.testesResultados.slice(0, 5).map((r, i) => (
                    <div key={i} className="px-3 py-1.5 flex justify-between gap-3">
                      <span className="text-slate-600">{r.template} → {r.para} <span className="text-slate-400">({(r.enviado_em || "").replace("T", " ").slice(0, 16)})</span></span>
                      <span className={r.ok ? "text-emerald-600 font-medium" : "text-red-600"}>{r.ok ? "enviado após aprovação" : `${r.status}${r.erro ? ` — ${r.erro}` : ""}`}</span>
                    </div>
                  ))}
                </div>
              )}
              {resultTpl && (
                <div className={`text-xs rounded-lg border px-3 py-2 ${resultTpl.ok ? "bg-emerald-50 border-emerald-200 text-emerald-700" : resultTpl.agendado ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="tpl-teste-resultado">
                  {resultTpl.ok ? `Teste enviado (${resultTpl.template}) para ${resultTpl.para}.` : resultTpl.agendado ? resultTpl.dica : `Falha (${resultTpl.template || ""}): ${resultTpl.erro}${resultTpl.dica ? ` — ${resultTpl.dica}` : ""}`}
                </div>
              )}
              {resultAniv && (
                <div className="text-sm border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="resultado-aniversarios">
                  <div className="px-3 py-2 font-medium text-slate-700">Enviados hoje: {resultAniv.enviados}{resultAniv.motivo ? ` · ${resultAniv.motivo}` : ""}</div>
                  {(resultAniv.itens || []).length === 0 && <div className="px-3 py-2 text-slate-400">Nenhum aniversariante hoje.</div>}
                  {(resultAniv.itens || []).map((it, i) => (
                    <div key={i} className="px-3 py-2 flex items-center justify-between gap-3">
                      <span className="text-slate-700">{it.nome} <span className="text-xs text-slate-400">({it.tipo})</span></span>
                      <span className={`text-xs font-medium ${it.status === "enviado" ? "text-emerald-600" : it.status === "ja_enviado" ? "text-slate-500" : "text-red-600"}`}>{it.status}{it.erro ? ` — ${it.erro}` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>


          <Card>
            <CardHeader><CardTitle className="text-base">Início do faturamento</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-500">
                O sistema <b>não considera nem cria</b> faturas em meses anteriores a esta data. As previsões e o espelho de conferência começam a partir do mês escolhido; a emissão de boleto continua dependendo do toggle do Asaas.
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label>Mês inicial (AAAA-MM)</Label>
                  <Input type="month" value={inicioMes} onChange={(e) => setInicioMes(e.target.value)} className="w-44" data-testid="input-inicio-faturamento" />
                </div>
                <Button onClick={salvarInicio} disabled={saving || inicioMes === (cfg.inicioFaturamento || "")} className="gap-2" data-testid="btn-salvar-inicio-faturamento">
                  {saving ? "Salvando..." : "Salvar mês inicial"}
                </Button>
              </div>
              <p className="text-xs text-slate-400" data-testid="inicio-faturamento-atual">
                Configuração atual: <b>{cfg.inicioFaturamento || "—"}</b>
              </p>

              <div className="border-t border-slate-100 pt-3 mt-1 space-y-2">
                <Label>Dias de fechamento antes do pagamento</Label>
                <p className="text-sm text-slate-500">
                  A fatura fecha <b>{cfg.fechamentoDiasAntes ?? 5}</b> dia(s) antes do dia de pagamento de cada residente. Os consumos contados vão do fechamento anterior até o fechamento atual. Ex.: paga dia 10 e {cfg.fechamentoDiasAntes ?? 5} dias → fecha dia {Math.max(1, 10 - (cfg.fechamentoDiasAntes ?? 5))}.
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <Label>Dias antes (0 a 27)</Label>
                    <Input type="number" min="0" max="27" value={diasFech} onChange={(e) => setDiasFech(e.target.value)} className="w-32" data-testid="input-fechamento-dias" />
                  </div>
                  <Button onClick={salvarDias} disabled={saving || String(diasFech) === String(cfg.fechamentoDiasAntes ?? 5)} className="gap-2" data-testid="btn-salvar-fechamento-dias">
                    {saving ? "Salvando..." : "Salvar dias"}
                  </Button>
                </div>
                <p className="text-xs text-slate-400" data-testid="fechamento-dias-atual">
                  Configuração atual: <b>{cfg.fechamentoDiasAntes ?? 5} dia(s)</b> antes do pagamento
                </p>
              </div>

              <div className="border-t border-slate-100 pt-3 mt-1 space-y-2">
                <Label>Chave Pix para recebimento das faturas</Label>
                <p className="text-sm text-slate-500">
                  No fechamento, o responsável recebe no WhatsApp o aviso e, em seguida, o <b>PDF da fatura com QR Code Pix</b> (QR + copia-e-cola) nesta chave, com o valor exato da fatura. O mesmo QR aparece no botão <b>Pix</b> em Faturas e nos PDFs enviados com o boleto.
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <Label>Chave Pix (CNPJ, CPF, e-mail, telefone ou aleatória)</Label>
                    <Input value={pixChave} onChange={(e) => setPixChave(e.target.value)} className="w-72" data-testid="input-pix-chave" />
                  </div>
                  <Button onClick={salvarPix} disabled={saving || (pixChave || "").replace(/\s/g, "") === (cfg.pixChave || "")} className="gap-2" data-testid="btn-salvar-pix-chave">
                    {saving ? "Salvando..." : "Salvar chave Pix"}
                  </Button>
                </div>
                <p className="text-xs text-slate-400" data-testid="pix-chave-atual">
                  Chave atual: <b>{cfg.pixChave || "—"}</b> · Favorecido: {cfg.pixFavorecido || "nome da Empresa (Configurações)"}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="teste" className="space-y-4 pt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Testar envio (não depende do liga/desliga)</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Seu e-mail de teste</Label>
                <div className="flex gap-2">
                  <Input type="email" placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="input-teste-email" />
                  <Button onClick={testarEmail} disabled={testando === "email" || !cfg.emailConfigurado} className="gap-2 shrink-0" data-testid="btn-teste-email">
                    <Send className="w-4 h-4" /> {testando === "email" ? "Enviando..." : "Enviar e-mail"}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Seu WhatsApp de teste (com DDD)</Label>
                <div className="flex gap-2">
                  <Input placeholder="11999998888" value={tel} onChange={(e) => setTel(e.target.value)} data-testid="input-teste-whatsapp" />
                  <Button onClick={testarWhatsapp} disabled={testando === "whatsapp" || !cfg.whatsappConfigurado} className="gap-2 shrink-0" data-testid="btn-teste-whatsapp">
                    <Send className="w-4 h-4" /> {testando === "whatsapp" ? "Enviando..." : "Enviar WhatsApp"}
                  </Button>
                </div>
                <p className="text-xs text-slate-400">O teste de WhatsApp usa o modelo padrão "hello_world" aprovado.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
